import { Box, CircularProgress, Typography } from "@mui/material";
import { useGetUserQuery, useLoginMutation } from "features/auth/auth";
import { useAppSelector } from "features/store";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import CustomButton from "./common/CustomButton";
import CustomPasswordField from "./common/CustomPasswordField";
import CustomTextField from "./common/CustomTextField";
import ForgotPasswordModal from "./ForgotPasswordModal";

type LoginInputs = {
  username: string;
  password: string;
};

export default function Login() {
  const [login, { isLoading, error, isSuccess }] = useLoginMutation();
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  const navigate = useNavigate();
  const { isAuthenticated, user, webAccessDenied } = useAppSelector((state) => state.auth);
  const { isFetching: isUserLoading } = useGetUserQuery(undefined, {
    skip: !isAuthenticated || !!webAccessDenied,
  });

  const isBusy = isLoading || isUserLoading;
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<LoginInputs>();

  const usernameValue = watch("username", "");

  const onSubmit: SubmitHandler<LoginInputs> = (data) => {
    login(data);
  };

  // Redirect already authenticated users
  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.is_pass_reset) {
        navigate("/");
      } else {
        navigate("/reset-password");
      }
    }
  }, [isAuthenticated, user, navigate]);

  // Navigate when login is successful
  useEffect(() => {
    if (isSuccess && isAuthenticated && user) {
      if (user.is_pass_reset) {
        navigate("/");
      } else {
        navigate("/reset-password");
      }
    }
  }, [isSuccess, isAuthenticated, user, navigate]);

  // Don't render login form if user is already authenticated
  if (isAuthenticated && user) {
    return null;
  }

  return (
    <Box
      marginTop={12}
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{
        width: "100%",
        maxWidth: 400,
        boxSizing: "border-box",
        px: { xs: 2, sm: 3 },
        bgcolor: "#fff",
        p: { xs: 2, sm: 4 },
        borderRadius: 1.5,
        border: "1px solid #e0e0e0",
      }}
    >
      <Typography
        component="h1"
        variant="h5"
        sx={{ fontSize: "32px" }}
        className="mb-4  whitespace-nowrap text-[rgb(31,93,107)]"
      >
        CalibrateHCM Sign In{" "}
      </Typography>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        marginTop={1}
      >
        <CustomTextField
          id="username"
          label="Username"
          autoComplete="username"
          autoFocus
          error={Boolean(errors.username)}
          helperText={errors.username ? errors.username.message : ""}
          {...register("username", {
            required: "Username is required",
          })}
        />
        <CustomPasswordField
          label="Password"
          id="password"
          autoComplete="current-password"
          error={Boolean(errors.password)}
          helperText={errors.password ? errors.password.message : ""}
          {...register("password", {
            required: "Password is required",
            // minLength: {
            //   value: 6,
            //   message: "Password must be at least 6 characters",
            // },
          })}
        />
        {isBusy ? (
          <div className="mt-4 flex justify-center">
            <CircularProgress />
          </div>
        ) : (
          <CustomButton type="submit">Sign In</CustomButton>
        )}

        {error && (
          <Typography
            color="error"
            style={{ marginTop: "16px", textAlign: "center" }}
          >
            {(() => {
              const err: any = error;
              const nonField = err?.data?.non_field_errors as string[] | undefined;
              const firstNonField = Array.isArray(nonField) ? nonField[0] : undefined;
              const detail = err?.data?.detail as string | undefined;
              const message = firstNonField || detail || "";

              if (message.includes("This account is inactive.")) {
                return "This account is inactive. Please contact support to reactivate your access.";
              }

              if (message.includes("This account has been archived.")) {
                return "This account has been archived. Please contact your administrator.";
              }

              return "Invalid Username or Password. Please try again.";
            })()}
          </Typography>
        )}

        <Box display="flex" justifyContent="center" mt={2}>
          <Typography
            component="span"
            sx={{
              color: "rgb(31, 93, 107)",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() => setForgotPasswordOpen(true)}
          >
            Forgot Password?
          </Typography>
        </Box>
      </Box>

      <ForgotPasswordModal
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
        initialUsername={usernameValue}
      />
    </Box>
  );
}
